import DOMPurify from 'isomorphic-dompurify'

/**
 * Sanitize HTML coming from the CMS before binding with v-html.
 * Uses DOMPurify (battle-tested allowlist parser) instead of regex stripping.
 */
export function sanitizeHtml(html: string): string {
  if (!html) return ''
  return DOMPurify.sanitize(html, {
    USE_PROFILES: { html: true },
    FORBID_TAGS: ['style', 'form', 'iframe', 'object', 'embed', 'base'],
    FORBID_ATTR: ['style', 'formaction'],
  })
}

/** Render CMS rich text while only creating embeds from validated placeholder nodes. */
export function renderRichContent(content: string): string {
  if (!content) return ''
  const html = looksLikeHtml(content) ? content : plainTextToHtml(content)
  const clean = sanitizeHtml(html)

  return clean.replace(/<figure\b([^>]*)>[\s\S]*?<\/figure>/gi, (figure, attributes: string) => {
    const provider = readAttribute(attributes, 'data-video-provider')
    const id = readAttribute(attributes, 'data-video-id')
    if (provider === 'youtube' && id && /^[A-Za-z0-9_-]{6,20}$/.test(id)) {
      return videoFrame(`https://www.youtube-nocookie.com/embed/${id}`, 'YouTube video', 'youtube')
    }
    if (provider === 'tiktok' && id && /^\d{5,30}$/.test(id)) {
      return videoFrame(`https://www.tiktok.com/player/v1/${id}`, 'TikTok video', 'tiktok')
    }
    return figure
  })
}

export function richTextToPlainText(content: string): string {
  if (!content) return ''
  return sanitizeHtml(content)
    .replace(/<figure\b[^>]*data-video-provider[\s\S]*?<\/figure>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/\s+/g, ' ')
    .trim()
}

function looksLikeHtml(value: string): boolean {
  return /<\/?(?:p|h[1-6]|ul|ol|li|blockquote|a|img|figure|br|hr|strong|em|u|s|pre|code)\b/i.test(value)
}

function plainTextToHtml(value: string): string {
  return value.split(/\n{2,}/).map(paragraph =>
    `<p>${escapeHtml(paragraph).replace(/\n/g, '<br>')}</p>`
  ).join('')
}

function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, char => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  })[char]!)
}

function readAttribute(attributes: string, name: string): string | null {
  const match = attributes.match(new RegExp(`\\b${name}=["']([^"']+)["']`, 'i'))
  return match?.[1] || null
}

function videoFrame(src: string, title: string, provider: 'youtube' | 'tiktok'): string {
  return `<div class="content-video content-video--${provider}"><iframe src="${src}" title="${title}" loading="lazy" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe></div>`
}
